import React, { useEffect, useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { LocationOption } from '../../types/Location';
import { loadAirportLocations, loadCityLocations } from '../../api/Location';
import { searchTransfers, TransferItem } from '../../api/Transfers';
import { TravellerInfo, Flight, Hotel, Place } from '../../types/Quotation';
import '../../css/HotelListModal.css';

interface TransferListModalProps {
  dayIndex: number;
  defaultDate?: string; // YYYY-MM-DD
  defaultPlace?: Place | null;
  travellers: TravellerInfo;
  placeOptions: Place[]; // unique cities from itinerary
  flights: Flight[]; // flattened flights across itinerary
  hotelsForDay: Hotel[]; // hotels on the selected day
  onClose: () => void;
  onSelect?: (transfer: any) => void;
}

const TransferListModal: React.FC<TransferListModalProps> = ({
  dayIndex,
  defaultDate,
  defaultPlace,
  travellers,
  placeOptions,
  flights,
  hotelsForDay,
  onClose,
  onSelect,
}) => {
  const totalPassengers = useMemo(
    () => (travellers?.adults || 0) + (travellers?.children || 0) + (travellers?.infants || 0),
    [travellers?.adults, travellers?.children, travellers?.infants]
  );

  // Form fields per API shape
  const [cityId, setCityId] = useState<string>('');
  const [cityLabel, setCityLabel] = useState<string>('');
  const [airport, setAirport] = useState<LocationOption | null>(null);
  const [hotelCode, setHotelCode] = useState<string>('');
  const [pickupFromAirport, setPickupFromAirport] = useState<boolean>(true);
  const [pickupDate, setPickupDate] = useState<string>(defaultDate || '');
  const [pickupTime, setPickupTime] = useState<string>('10:30');
  const [passengers, setPassengers] = useState<number>(Math.max(1, totalPassengers || 1));
  const [bags, setBags] = useState<number>(2);

  const [results, setResults] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Infer defaults from itinerary: city, airport (to), hotel for the day
  useEffect(() => {
    try {
      // City ID default from the day's place (value is expected to be city code/id)
      if (defaultPlace?.value) {
        setCityId(String(defaultPlace.value));
        setCityLabel(String(defaultPlace.label || ''));
      }
      // Airport code default from the first available flight's destination
      const flightTo = flights?.find(f => (f?.to || '').trim().length > 0)?.to?.trim();
      if (flightTo) {
        const codeGuess = /^[A-Za-z]{3}$/.test(flightTo) ? flightTo.toUpperCase() : '';
        if (codeGuess) {
          setAirport({ label: codeGuess, value: codeGuess, type: 'airport' });
        } else {
          // Try resolve via location API using destination text
          loadAirportLocations(flightTo).then(opts => {
            const match = (opts || []).find(o => o.type === 'airport');
            if (match) setAirport(match);
          }).catch(() => void 0);
        }
      }
      // Hotel code default from the first hotel on the day
      if (Array.isArray(hotelsForDay) && hotelsForDay.length > 0) {
        setHotelCode(hotelsForDay[0].hotelId);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolve airport code (IATA) from current selection if needed
      const ensureAirportCode = async (): Promise<string> => {
        const raw = (airport?.value || '').trim();
        if (/^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();
        const query = (airport?.label || raw || cityLabel || defaultPlace?.label || '').trim();
        if (query.length < 3) return '';
        try {
          const opts = await loadAirportLocations(query);
          const match = (opts || []).find(o => o.type === 'airport');
          return (match?.value || '').toUpperCase();
        } catch {
          return '';
        }
      };
      // Ensure we pass a city CODE (value from Location API), not a name
      let effectiveCityId = (cityId || '').trim();
      const looksLikeCode = /^\d+$/.test(effectiveCityId) || /^[A-Za-z]{3}$/.test(effectiveCityId);
      if (!looksLikeCode) {
        try {
          const labelToResolve = (cityLabel || defaultPlace?.label || effectiveCityId).trim();
          if (labelToResolve.length >= 3) {
            const opts = await loadCityLocations(labelToResolve);
            const match = (opts || []).find(o => o.type === 'city');
            if (match?.value) {
              effectiveCityId = match.value;
              setCityId(match.value);
            }
          }
        } catch {}
      }
      const effectiveAirportCode = await ensureAirportCode();
      const payload = {
        cityId: effectiveCityId,
        airportCode: effectiveAirportCode,
        hotelCode: hotelCode.trim(),
        pickupFromAirport,
        pickupDate: (pickupDate || '').trim(),
        pickupTime: (pickupTime || '').trim(),
        passengers: Number(passengers || 0),
        bags: Number(bags || 0),
      };
      const resp = await searchTransfers(payload);
      setResults(resp.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch transfers');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Flatten each transfer item into vehicle-level options for clearer listing
  const vehicleOptions = useMemo(() => {
    const list: Array<{
      key: string;
      provider: string;
      transferName: string;
      vehicleName: string;
      pax?: number;
      bags?: number;
      price?: number;
      currency?: string;
      raw: any;
      approxTime?: number;
    }> = [];
    results.forEach((it, idx) => {
      const vehicles = Array.isArray(it?.meta?.Vehicles) ? it.meta.Vehicles : [];
      const approxTime = typeof it?.meta?.ApproximateTransferTime === 'number' ? it.meta.ApproximateTransferTime : undefined;
      if (vehicles.length === 0) {
        list.push({
          key: `${idx}`,
          provider: it.provider,
          transferName: it?.meta?.TransferName || it.name || 'Transfer',
          vehicleName: 'Vehicle',
          pax: undefined,
          bags: undefined,
          price: it.price,
          currency: it.currency,
          raw: it,
          approxTime,
        });
      } else {
        vehicles.forEach((v: any, vi: number) => {
          const price = v?.TransferPrice?.OfferedPrice ?? v?.TransferPrice?.PublishedPrice ?? v?.TransferPrice?.BasePrice;
          const currency = v?.TransferPrice?.CurrencyCode || it?.currency || 'INR';
          list.push({
            key: `${idx}-${vi}`,
            provider: it.provider,
            transferName: it?.meta?.TransferName || it.name || 'Transfer',
            vehicleName: v?.Vehicle || 'Vehicle',
            pax: v?.VehicleMaximumPassengers,
            bags: v?.VehicleMaximumLuggage,
            price,
            currency,
            raw: { ...it, vehicle: v },
            approxTime,
          });
        });
      }
    });
    return list;
  }, [results]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal hotel-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="hotel-search-form">
          <div className="hs-field">
            <label>City</label>
            <select
              value={cityId}
              onChange={e => {
                const val = e.target.value;
                setCityId(val);
                const lbl = placeOptions.find(p => p.value === val)?.label || '';
                setCityLabel(lbl);
              }}
              title="City from itinerary"
            >
              <option value="">Select city</option>
              {placeOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.value} — {p.label}</option>
              ))}
            </select>
          </div>
          <div className="hs-field">
            <label>Airport</label>
            <AsyncSelect<LocationOption, false>
              cacheOptions
              defaultOptions
              loadOptions={loadAirportLocations}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              value={airport}
              onChange={opt => setAirport(opt as LocationOption)}
              placeholder="Type airport name/code"
              classNamePrefix="rs"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                menu: (base) => ({ ...base, zIndex: 2000 }),
              }}
            />
          </div>
          <div className="hs-field">
            <label>Hotel</label>
            <select
              value={hotelCode}
              onChange={e => setHotelCode(e.target.value)}
              title="Hotel from selected day"
            >
              <option value="">Select hotel</option>
              {hotelsForDay.map((h) => (
                <option key={h.hotelId} value={h.hotelId}>{h.hotelId} — {h.name}</option>
              ))}
            </select>
          </div>
          <div className="hs-field">
            <label>Pickup From Airport</label>
            <select value={pickupFromAirport ? '1' : '0'} onChange={e => setPickupFromAirport(e.target.value === '1')}>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
          <div className="hs-field">
            <label>Pickup Date</label>
            <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)} />
          </div>
          <div className="hs-field">
            <label>Pickup Time</label>
            <input type="time" value={pickupTime} onChange={e => setPickupTime(e.target.value)} />
          </div>
          <div className="hs-field">
            <label>Passengers</label>
            <input type="number" min={1} value={passengers} onChange={e => setPassengers(Number(e.target.value || 1))} />
          </div>
          <div className="hs-field">
            <label>Bags</label>
            <input type="number" min={0} value={bags} onChange={e => setBags(Number(e.target.value || 0))} />
          </div>
          <button className="hs-search-btn" onClick={doSearch}>Search</button>
        </div>

        <div className="hotel-list">
          {loading && <p className="loading">Searching transfers…</p>}
          {error && <p className="error">{error}</p>}
          {!loading && !error && vehicleOptions.length === 0 && (
            <p className="no-results">No transfers found. Adjust your search and try again.</p>
          )}
          {!loading && !error && vehicleOptions.map((v, i) => {
            const symbol = v.currency === 'USD' ? '$' : v.currency === 'EUR' ? '€' : '₹';
            return (
              <div className="hotel-card" key={v.key}>
                <div className="hotel-main">
                  <img
                    src={'/placeholder-car.png'}
                    alt={v.vehicleName}
                    className="hotel-thumbnail"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div className="hotel-info">
                    <h3 className="hotel-name">{v.transferName} — {v.vehicleName}</h3>
                    <div className="hotel-amenities">
                      {typeof v.pax === 'number' && (
                        <span className="amenity-badge">Max {v.pax} pax</span>
                      )}
                      {typeof v.bags === 'number' && (
                        <span className="amenity-badge">Max {v.bags} bags</span>
                      )}
                      {typeof v.approxTime === 'number' && (
                        <span className="amenity-badge">~{v.approxTime} hr</span>
                      )}
                      {/* Provider hidden per requirements */}
                    </div>
                  </div>
                  <div className="hotel-rate">
                    <span className="hotel-price">{symbol}{Number(v.price || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <div className="hotel-action">
                  <button
                    className="select-btn"
                    onClick={() => {
                      const selection = v.raw || {};
                      // Attempt to resolve friendly names
                      const ai = selection?.meta?.PickUp?.PickUpDetailName || selection?.meta?.DropOff?.DropOffDetailName;
                      const airportLabel = airport?.label || ai || '';
                      const hotelFromList = hotelsForDay.find(h => h.hotelId === hotelCode);
                      const hotelFriendly = hotelFromList?.name || selection?.meta?.PickUp?.DropOffDetailName || selection?.meta?.DropOff?.DropOffDetailName;
                      const transfer = {
                        provider: selection.provider || v.provider,
                        name: selection?.meta?.TransferName || v.transferName,
                        vehicle: selection?.vehicle?.Vehicle || v.vehicleName,
                        price: v.price,
                        currency: v.currency,
                        pickupDate,
                        pickupTime,
                        pickupFromAirport,
                        cityId,
                        airportCode: airport?.value || '',
                        airportName: airportLabel || undefined,
                        hotelCode,
                        hotelName: hotelFriendly || undefined,
                        meta: selection,
                      };
                      if (onSelect) onSelect(transfer);
                      onClose();
                    }}
                  >
                    Select
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TransferListModal;
